import { defineStore } from 'pinia'
import _ from 'lodash'


import { resourceApi } from './common/api'
import { rules, defaultItemsPerPage } from './common/commonFunctions'

const bundleApi = resourceApi.extend('bundle')

export const useBundle = defineStore('bundle', {
  persist: [
    {
      key: (context) => `${__APP_NAME__}_${context}`,
      pick: ['itemsPerPage'],
      storage: localStorage
    }
  ],

  state() {
    return {
      rules: rules.bundle,
      isLoadingActiveBundles: false,
      errorActiveBundles: null,
      statusActiveBundles: null,
      isLoadingArchivedBundles: false,
      errorArchivedBundles: null,
      statusArchivedBundles: null,
      isLoadingArchivingBundles: false,
      errorArchivingBundles: null,
      statusArchivingBundles: null,
      isLoadingRestoringBundles: false,
      errorRestoringBundles: null,
      statusRestoringBundles: null,
      isLoadingEditingBundle: false,
      errorEditingBundle: null,
      statusEditingBundle: null,
      isLoadingCreatingBundle: false,
      errorCreatingBundle: null,
      statusCreatingBundle: null,
      isActionMultiple: null,
      fetchBundlesFrequency: 300000,
      fetchActiveBundlesInterval: null,
      fetchArchivedBundlesInterval: null,
      itemsPerPage: {
        active: defaultItemsPerPage,
        archived: defaultItemsPerPage
      },
      activeBundles: null,
      archivedBundles: null,
      tabs: [
        {
          value: 'active',
          title: 'Active'
        },
        {
          value: 'archived',
          title: 'Archived'
        }
      ],
      channels: [
        {
          title: 'Dev',
          value: 'dev',
          icon: 'mdi-code-braces-box'
        },
        {
          title: 'QA',
          value: 'qa',
          icon: 'ms:glyphs'
        },
        {
          title: 'Beta',
          value: 'beta',
          icon: 'mdi-beta'
        },
        {
          title: 'Public',
          value: 'public',
          icon: 'mdi-earth'
        }
      ],
      selectedBundles: null,
      selectedChannels: null,
      tableData: [],
      editBundle: null,
      currentTab: null
    }
  },

  getters: {
    getArchivedBundles: ({ archivedBundles }) => {
      return archivedBundles
    },
    getRules: ({ rules }) => {
      return rules
    },
    getFetchBundlesFrequency: ({ fetchBundlesFrequency }) => {
      return fetchBundlesFrequency
    },
    getFetchActiveBundlesInterval: ({ fetchActiveBundlesInterval }) => {
      return fetchActiveBundlesInterval
    },
    getFetchArchivedBundlesInterval: ({ fetchArchivedBundlesInterval }) => {
      return fetchArchivedBundlesInterval
    },
    getIsLoadingActiveBundles: ({ isLoadingActiveBundles }) => {
      return isLoadingActiveBundles
    },
    getIsLoadingArchivedBundles: ({ isLoadingArchivedBundles }) => {
      return isLoadingArchivedBundles
    },
    getIsLoadingArchivingBundles: ({ isLoadingArchivingBundles }) => {
      return isLoadingArchivingBundles
    },
    getIsLoadingRestoringBundles: ({ isLoadingRestoringBundles }) => {
      return isLoadingRestoringBundles
    },
    getIsLoadingEditingBundle: ({ isLoadingEditingBundle }) => {
      return isLoadingEditingBundle
    },
    getIsLoadingCreatingBundle: ({ isLoadingCreatingBundle }) => {
      return isLoadingCreatingBundle
    },
    getData: ({
      activeBundles,
      archivedBundles,
      tabs,
      channels,
      itemsPerPage
    }) => {
      if (_.isEmpty(activeBundles) && _.isEmpty(archivedBundles)) return null

      const _channelsByValue = _.keyBy(channels, 'value')
      const headers = [
        {
          title: 'Simulation',
          key: 'simulation',
          value: (item) => item?.simulationData?.title
        },
        {
          title: 'Channel',
          key: 'channel',
          value: (item) =>
            _.has(item, 'channel') && _.has(_channelsByValue, item.channel)
              ? _channelsByValue[item.channel].title
              : item?.channel
        },
        {
          title: 'Filename',
          key: 'filename',
          value: 'filename'
        },
        {
          title: 'Version',
          key: 'version',
          value: 'version'
        },
        {
          title: 'Checksum',
          key: 'checksum',
          value: 'checksum'
        }
        // updateTs,
        // date of Publish
        // look into possibility to add/remove columns
        //
      ]
      const filterKeys = ['simulation', 'channel', 'filename', 'checksum']
      let returnValue = {
        headers,
        channels,
        filterKeys,
        itemsPerPage: {
          active: itemsPerPage.active,
          default: defaultItemsPerPage
        },
        data: {
          active: activeBundles
        },
        count: {
          active: activeBundles?.length ?? 0
        }
      }
      if (archivedBundles != null && archivedBundles?.length > 0) {
        returnValue.itemsPerPage.archived = itemsPerPage.archived
        returnValue.data.archived = archivedBundles
        returnValue.count.archived = archivedBundles.length
      }
      const dataKeys = _.keys(returnValue.data)
      returnValue.tabs = tabs.filter(({ value }) => dataKeys.includes(value))
      return returnValue
    },
    getTableData: ({ tableData }) => tableData,
    getSelectedChannels: ({ selectedChannels }) => selectedChannels,
    getChannelSummary: ({ selectedChannels }) => {
      if (_.isEmpty(selectedChannels)) return '(None)'
      return `(${selectedChannels.length} selected)`
    },
    getIsLoadingMessage: ({
      isLoadingArchivingBundles,
      isLoadingRestoringBundles,
      isLoadingEditingBundle,
      isLoadingCreatingBundle,
      isActionMultiple
    }) => {
      if (isLoadingArchivingBundles || isLoadingRestoringBundles)
        return isActionMultiple
          ? `${isLoadingArchivingBundles ? 'Archiving' : 'Restoring'} simulation bundles...`
          : `${isLoadingArchivingBundles ? 'Archiving' : 'Restoring'} simulation bundle...`
      else if (isLoadingEditingBundle) return 'Updating simulation bundle...'
      else if (isLoadingCreatingBundle) return 'Creating simulation bundle...'
      else return null
    },
    getErrors: ({
      errorActiveBundles,
      errorArchivedBundles,
      errorArchivingBundles,
      errorRestoringBundles,
      errorEditingBundle,
      errorCreatingBundle
    }) => {
      let returnValue = []
      if (errorActiveBundles)
        returnValue.push({
          text: errorActiveBundles,
          key: 'activeBundles'
        })
      if (errorArchivedBundles)
        returnValue.push({
          text: errorArchivedBundles,
          key: 'archivedBundles'
        })
      if (errorArchivingBundles)
        returnValue.push({
          text: errorArchivingBundles,
          key: 'archivingBundles'
        })
      if (errorRestoringBundles)
        returnValue.push({
          text: errorRestoringBundles,
          key: 'restoringBundles'
        })
      if (errorEditingBundle)
        returnValue.push({
          text: errorEditingBundle,
          key: 'editingBundle'
        })
      if (errorCreatingBundle)
        returnValue.push({
          text: errorCreatingBundle,
          key: 'creatingBundle'
        })
      return returnValue
    }
  },

  actions: {
    async fetchActiveBundles(withLoader = true) {
      if (this.errorActiveBundles != null) this.errorActiveBundles = null
      this.statusActiveBundles = null
      if (withLoader) this.isLoadingActiveBundles = true
      try {
        const result = await bundleApi.get('', { params: { active: 1 } })
        const { error, bundle } = result.data.response
        this.statusActiveBundles = result.data.status
        if (error) this.errorActiveBundles = error.message
        else this.activeBundles = bundle
      } catch (e) {
        this.errorActiveBundles =
          e?.response?.data?.response?.error?.message ||
          e.message ||
          String(e) ||
          JSON.stringify(e)
      }
      this.isLoadingActiveBundles = false
    },
    async fetchArchivedBundles(withLoader = true) {
      if (this.errorArchivedBundles != null) this.errorArchivedBundles = null
      this.statusArchivedBundles = null
      if (withLoader) this.isLoadingArchivedBundles = true
      try {
        const result = await bundleApi.get('', { params: { active: 0 } })
        const { error, bundle } = result.data.response
        this.statusArchivedBundles = result.data.status
        if (error) this.errorArchivedBundles = error.message
        else this.archivedBundles = bundle
      } catch (e) {
        this.errorArchivedBundles =
          e?.response?.data?.response?.error?.message ||
          e.message ||
          String(e) ||
          JSON.stringify(e)
      }
      this.isLoadingArchivedBundles = false
    },
    setFetchActiveBundlesInterval(newValue) {
      if (newValue == null && this.fetchActiveBundlesInterval != null)
        clearInterval(this.fetchActiveBundlesInterval)
      this.fetchActiveBundlesInterval = newValue
    },
    setFetchArchivedBundlesInterval(newValue) {
      if (newValue == null && this.fetchArchivedBundlesInterval != null)
        clearInterval(this.fetchArchivedBundlesInterval)
      this.fetchArchivedBundlesInterval = newValue
    },
    setEditBundle(newValue) {
      this.editBundle = newValue
    },
    setTableData() {
      if (_.isEmpty(this.activeBundles) && _.isEmpty(this.archivedBundles))
        this.tableData = []
      else this.tableData = this.getData.data?.[this.currentTab] ?? []
    },
    setItemsPerPage(itemsPerPage) {
      this.itemsPerPage[this.currentTab] = itemsPerPage
    },
    initSelectedChannels() {
      this.selectedChannels = _.map(this.channels, 'value')
    },
    cancelError(key) {
      switch (key) {
        case 'activeBundles':
          this.errorActiveBundles = null
          break
        case 'archivedBundles':
          this.errorArchivedBundles = null
          break
        case 'archivingBundles':
          this.errorArchivingBundles = null
          break
        case 'restoringBundles':
          this.errorRestoringBundles = null
          break
        case 'editingBundle':
          this.errorEditingBundle = null
          break
        case 'creatingBundle':
          this.errorCreatingBundle = null
          break
        default:
          break
      }
    }
  }
})